import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { getOrgContextAdminSetup, updateOrgContextAdminSetup, fetchOrgMetaData } from '../../../Api/apiService';
import useAuthStore from 'Store/useAuthStore';

const useOrgContext = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrgContext = async () => {
    setLoading(true);
    try {
      const response = await getOrgContextAdminSetup();
      // API returns {success: true, data: {completion_percentage, cards}}
      // Extract the nested data object
      const actualData = response.data?.data || response.data;
      setData(actualData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load organization context');
      notification.error({ 
        message: 'Failed to load organization context',
        description: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOrgContext = async (cardKey, changedFields) => {
    try {
      // Only send the changed fields in the PATCH request
      const payload = { [cardKey]: changedFields };
      const response = await updateOrgContextAdminSetup(payload);
      
      // Extract the nested data object from response
      const actualData = response.data?.data || response.data;
      
      // Update local state with the full response
      setData(actualData);

      // Refresh the global org metadata in the store so branding (header logo),
      // etc. reflect the change immediately. The backend already invalidated its
      // 6h Redis cache on this update, so this re-fetch returns the fresh values.
      try {
        const freshMeta = await fetchOrgMetaData();
        if (freshMeta) useAuthStore.getState().setOrgMetaData(freshMeta);
      } catch (metaErr) {
        // Non-fatal: the save succeeded; metadata will refresh on next login.
        console.error('useOrgContext - org metadata refresh failed:', metaErr);
      }

      notification.success({ message: 'Saved successfully' });
      return { success: true, data: actualData };
    } catch (err) {
      notification.error({ 
        message: 'Failed to save', 
        description: err.response?.data?.message || err.message 
      });
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchOrgContext();
  }, []);

  return { data, loading, error, refetch: fetchOrgContext, updateOrgContext };
};

export default useOrgContext;
